import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert } from '../components/auth/Alert.js';
import { OtpInput } from '../components/auth/OtpInput.js';
import { PasswordInput } from '../components/auth/PasswordInput.js';
import { PasswordStrengthMeter } from '../components/auth/PasswordStrengthMeter.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { passwordResetConfirm } from '../lib/auth-api.js';

type AlertState =
  | { kind: 'weak_password' }
  | { kind: 'invalid_totp' }
  | { kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { kind: 'server_error' }
  | { kind: 'network_error'; message: string }
  | null;

const MIN_PASSWORD_LENGTH = 12;
// The backend's regex is /^[a-f0-9]{64}$/. We bail much earlier on the
// client when the token is too short to plausibly be valid — 32 chars
// is a safe-and-generous floor that catches "user pasted the wrong
// thing" cases without rejecting future format changes.
const MIN_PLAUSIBLE_TOKEN_LENGTH = 32;
const REDIRECT_SECONDS = 5;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  if (token.length < MIN_PLAUSIBLE_TOKEN_LENGTH) {
    return <InvalidTokenView />;
  }

  return <ResetForm token={token} />;
}

function InvalidTokenView() {
  return (
    <AuthLayout title="Réinitialiser le mot de passe">
      <Alert variant="error" title="Lien invalide ou expiré">
        Ce lien est invalide ou a expiré. Demandez un nouveau lien depuis la page de
        réinitialisation.
      </Alert>
      <Link
        to="/reset-password-request"
        className="btn-primary w-full mt-6"
        data-testid="reset-confirm-request-new-link"
      >
        Demander un nouveau lien
      </Link>
    </AuthLayout>
  );
}

function ResetForm({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [totp, setTotp] = useState('');
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<AlertState>(null);
  const [done, setDone] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const passwordTooShort = password.length < MIN_PASSWORD_LENGTH;
  const passwordsMatch = password.length > 0 && password === confirm;
  const totpComplete = totp.length === 6;
  const canSubmit = !submitting && !passwordTooShort && passwordsMatch && totpComplete;

  if (tokenInvalid) return <InvalidTokenView />;
  if (done) return <SuccessView />;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setAlert(null);
    const result = await passwordResetConfirm(token, password, totp);
    if (result.ok) {
      setSubmitting(false);
      setDone(true);
      return;
    }
    setSubmitting(false);
    if (result.kind === 'invalid_or_expired_token') {
      setTokenInvalid(true);
      return;
    }
    if (result.kind === 'invalid_totp') {
      // Clear and re-focus the OTP — the page-level pattern from
      // mfa-verify / mfa-setup.
      setTotp('');
      setOtpResetKey((k) => k + 1);
      setAlert({ kind: 'invalid_totp' });
      return;
    }
    if (result.kind === 'weak_password') {
      setAlert({ kind: 'weak_password' });
      return;
    }
    if (result.kind === 'rate_limited') {
      setAlert({ kind: 'rate_limited', retryAfterSeconds: result.retryAfterSeconds });
      return;
    }
    if (result.kind === 'network_error') {
      setAlert({ kind: 'network_error', message: result.message });
      return;
    }
    setAlert({ kind: 'server_error' });
  }

  return (
    <AuthLayout
      title="Réinitialiser le mot de passe"
      subtitle="Définissez un nouveau mot de passe pour votre compte."
    >
      {alert ? (
        <div className="mb-4">
          <AlertView alert={alert} />
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <PasswordInput
          label="Nouveau mot de passe"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          helper="12 caractères minimum. Évitez les mots de passe déjà utilisés ailleurs."
          required
          data-testid="reset-confirm-password"
        />
        <PasswordStrengthMeter password={password} />

        <div className="mt-4">
          <PasswordInput
            label="Confirmer le nouveau mot de passe"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={
              confirm.length > 0 && !passwordsMatch
                ? 'Les deux mots de passe ne correspondent pas.'
                : undefined
            }
            required
            data-testid="reset-confirm-password2"
          />
        </div>

        <div className="mt-6">
          <p className="text-sm font-medium text-ink mb-1.5">Code d’authentification</p>
          <p className="text-xs text-ink-tertiary mb-2">
            Saisissez le code à 6 chiffres de votre application d’authentification pour confirmer ce
            changement.
          </p>
          <OtpInput
            key={otpResetKey}
            ariaLabel="Code d’authentification à 6 chiffres"
            disabled={submitting}
            onChange={setTotp}
            onComplete={setTotp}
            autoFocus={false}
          />
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          aria-busy={submitting || undefined}
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="reset-confirm-submit"
        >
          {submitting ? 'Réinitialisation…' : 'Réinitialiser le mot de passe'}
        </button>
      </form>
    </AuthLayout>
  );
}

function SuccessView() {
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (remaining <= 0) {
      navigate('/login');
      return;
    }
    const id = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining, navigate]);

  return (
    <AuthLayout title="Mot de passe réinitialisé">
      <Alert variant="success" title="Mot de passe mis à jour">
        Vous pouvez maintenant vous connecter avec votre nouveau mot de passe. Toutes vos sessions
        précédentes ont été déconnectées par sécurité.
      </Alert>
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="btn-primary w-full mt-6"
        data-testid="reset-confirm-back-to-login"
      >
        Se connecter
      </button>
      <p
        className="mt-3 text-center text-xs text-ink-tertiary"
        data-testid="reset-confirm-redirect-counter"
      >
        Redirection dans {remaining}…
      </p>
    </AuthLayout>
  );
}

function AlertView({ alert }: { alert: NonNullable<AlertState> }) {
  if (alert.kind === 'weak_password') {
    return (
      <Alert variant="error">
        Ce mot de passe est trop courant ou trop court. Choisissez-en un différent.
      </Alert>
    );
  }
  if (alert.kind === 'invalid_totp') {
    return (
      <Alert variant="error">
        Code d’authentification incorrect. Vérifiez sur votre application.
      </Alert>
    );
  }
  if (alert.kind === 'rate_limited') {
    const sec = alert.retryAfterSeconds;
    const body =
      sec === null
        ? 'Trop de tentatives. Patientez un instant avant de réessayer.'
        : `Trop de tentatives. Patientez environ ${Math.max(1, sec)} seconde${
            sec > 1 ? 's' : ''
          } avant de réessayer.`;
    return (
      <Alert variant="warning" title="Trop de tentatives">
        {body}
      </Alert>
    );
  }
  if (alert.kind === 'network_error') {
    return <Alert variant="error">{alert.message}</Alert>;
  }
  return <Alert variant="error">Une erreur est survenue. Réessayez plus tard.</Alert>;
}
